'use client';
import { classes } from '@/utils/classes/classes';
import type { StaticContentBodyItem } from '@/types/aggregation/static';
import Accordion from './accordion';
import { useGlobalContext } from '@/store/contexts/global-context';

type TableOfContentsProps = {
    content: StaticContentBodyItem[];
};

type Headings = { header: string; content: string[] };

const getHeadings = (items: StaticContentBodyItem[]) => {
    const res: Headings[] = [];

    const collectHeadings = (obj: StaticContentBodyItem) => {
        if (!obj || typeof obj !== 'object' || ['faq_section', 'zone'].includes(obj?.slice_type)) {
            return;
        }
        if (obj.type === 'heading2' && obj.text) {
            res.push({ header: obj.text, content: [] });
        } else if (obj.type === 'heading3' && obj.text && res.length) {
            res[res.length - 1].content.push(obj.text);
        }
        Object.values(obj).forEach(collectHeadings);
    };

    items.forEach(collectHeadings);

    return res;
};

const TableOfContents = ({ content }: TableOfContentsProps) => {
    const headings = getHeadings(content);
    const { isMobile } = useGlobalContext();

    const handleClick = (p: string) => {
        if (!p?.length) {
            return;
        }

        const el = document.getElementById(p.replaceAll(' ', '_'));
        if (el) {
            const rect = el.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            window.scrollTo({ top: rect.top + scrollTop - 90, behavior: 'smooth' });
        }
    };

    return (
        <summary className={classes('table-of-contents-wrapper', isMobile && 'toc-wrapper-mobile')}>
            <Accordion
                header="Table of Contents"
                body={headings.map((item, idx) => (
                    <Accordion
                        key={idx}
                        header={item.header}
                        handleHeaderClicked={() => handleClick(item.header)}
                        body={
                            item.content.length ? (
                                <ul>
                                    {item.content.map((el, k) => (
                                        <li key={k} onClick={() => handleClick(el)}>
                                            {el}
                                        </li>
                                    ))}
                                </ul>
                            ) : null
                        }
                    />
                ))}
                isTopLevel={true}
                isExpandedByDefault={!isMobile}
            />
        </summary>
    );
};

export default TableOfContents;
